import { createSelector } from '@reduxjs/toolkit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import ProductIntervalSelector from '@/components/products/ProductIntervalSelector';
import ProductPrice from '@/components/products/ProductPrice';
import { setProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/actions';
import { formatPrice, getCurrencySymbol, parseBenefits } from '@/components/products/utils';
import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import type { ProductInterval } from '@/queries/products/types';
import { AppState, useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import PromotedProductSection from './PromotedProductSection';
import UserProductImage from './UserProductImage';
import { EXPLORER_PAIR_LOOKUP_KEYS, selectProductsMap, SPONSORSHIP_TIER_ONE_LOOKUP_KEYS, type UpsellType } from './utils';

interface UpsellPromotedProductSectionProps {
  upsellType: UpsellType;
  habitatId?: string;
}

const DayPassUpsellBlock = () => {
  const { data: dayPassProduct, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: 'all-habitats-day-pass' }),
    select: (data) => data.list[0],
  });

  const benefits = useMemo(() => parseBenefits(dayPassProduct), [dayPassProduct]);

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  if (isLoading || !dayPassProduct) return null;

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="All-Access Pass & Guided Experience"
      subtitle="Go on an interactive expedition through the best habitats at your pace, plus 24h to explore on your own."
      priceRow={<ProductPrice currency={dayPassProduct.price.currency} price={dayPassProduct.price.price} />}
      benefitLines={benefits}
      primaryButton={{
        label: 'Select Pass',
        disabled: isPending || !dayPassProduct.eligibility?.allowed,
        loading: isPending,
        dataId: 'upsell-day-pass-button',
        onClick: () => {
          checkout({
            productId: dayPassProduct._id,
            priceId: dayPassProduct.price.priceId,
          });
          trackMP('request-checkout', {
            productId: dayPassProduct._id,
            priceId: dayPassProduct.price.priceId,
            productLookupKey: dayPassProduct.productLookupKey,
            entryPoint: 'my-purchases-upsell',
          });
        },
      }}
      helperContent="Note: Journey lengths vary — while most take around 60 minutes, you're free to go as quickly or leisurely as you like. 24-hours begin at the start of your guided journey."
    />
  );
};

const ExplorerMonthlyAnnualUpsellBlock = () => {
  const [interval, setInterval] = useState<ProductInterval>('month');

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey: EXPLORER_PAIR_LOOKUP_KEYS }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-member');
  const yearlyProduct = productsMap?.get('annual-member');
  const explorerProduct = interval === 'month' ? monthlyProduct : yearlyProduct;

  const benefits = useMemo(() => parseBenefits(explorerProduct), [explorerProduct]);

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  if (isLoading || !explorerProduct) return null;

  return (
    <PromotedProductSection
      image={<UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />}
      title="Explorer Plan"
      subtitle="Unlimited discovery and delight."
      priceRow={
        <>
          <ProductPrice currency={explorerProduct.price.currency} price={explorerProduct.price.price} />
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label: 'Select Plan',
        disabled: isPending || !explorerProduct.eligibility?.allowed,
        loading: isPending,
        dataId: 'upsell-explorer-button',
        onClick: () => {
          checkout({
            productId: explorerProduct._id,
            priceId: explorerProduct.price.priceId,
          });
          trackMP('request-checkout', {
            productId: explorerProduct._id,
            priceId: explorerProduct.price.priceId,
            productLookupKey: explorerProduct.productLookupKey,
            entryPoint: 'my-purchases-upsell',
          });
        },
      }}
      helperContent={interval === 'month' ? 'Minimum 3 months.' : 'Cancel anytime.'}
    />
  );
};

interface SponsorshipUpsellBlockProps {
  habitatId: string;
}

const SponsorshipUpsellBlock = ({ habitatId }: SponsorshipUpsellBlockProps) => {
  const dispatch = useDispatch();
  const [interval, setInterval] = useState<ProductInterval>('month');

  const selectHabitat = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => allHabitats.find((h) => h._id === habitatId),
      ),
    [habitatId],
  );
  const habitat = useSelector(selectHabitat);

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey: SPONSORSHIP_TIER_ONE_LOOKUP_KEYS,
    }),
    select: selectProductsMap,
  });

  const monthlyProduct = productsMap?.get('monthly-supporter-tier-1');
  const yearlyProduct = productsMap?.get('annual-supporter-tier-1');
  const tierOneProduct = interval === 'month' ? monthlyProduct : yearlyProduct;

  const benefits = useMemo(() => parseBenefits(tierOneProduct), [tierOneProduct]);

  if (isLoading || !tierOneProduct || !habitat) return null;

  return (
    <PromotedProductSection
      image={
        <UserProductImage
          className="size-11 shrink-0 desktop:size-[60px]"
          imageURL={habitat.profileImage}
          title={habitat.animal}
        />
      }
      title="Habitat Supporter"
      subtitle={`Unlimited Access to ${habitat.animal} at ${habitat.zoo.name}`}
      priceRow={
        <>
          <span className="inline-flex items-center text-2xl/6 font-medium text-secondary-green">
            From {getCurrencySymbol(tierOneProduct.price.currency)}
            {formatPrice(tierOneProduct.price.price)}
          </span>
          <ProductIntervalSelector value={interval} onChange={setInterval} />
        </>
      }
      benefitLines={benefits}
      primaryButton={{
        label: 'Purchase Plan',
        dataId: 'upsell-sponsorship-button',
        onClick: () => {
          dispatch(
            setProductsPromptModalState({
              primaryProductType: 'sponsorship',
              habitatId,
              entryPoint: 'my-purchases-upsell',
            }),
          );
        },
      }}
      helperContent="Your upgraded access starts right away, and you'll only be charged the prorated difference for the rest of your current billing cycle."
    />
  );
};

const UpsellPromotedProductSection = ({ upsellType, habitatId }: UpsellPromotedProductSectionProps) => {
  switch (upsellType) {
    case 'day-pass':
      return <DayPassUpsellBlock />;
    case 'explorer':
      return <ExplorerMonthlyAnnualUpsellBlock />;
    case 'sponsorship':
      return habitatId ? <SponsorshipUpsellBlock habitatId={habitatId} /> : null;
    default:
      return null;
  }
};

export default UpsellPromotedProductSection;
